import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import type { Cluster, ClusterNote, Identity, Plate } from '../types'
import { imageUrl } from '../api'
import { useClipReady } from '../useClipReady'
import { isInProgress } from '../activity'

const clockOf = (epoch: number) => new Date(epoch * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })

// lengthOf is how long something ran, as "4m 10s" or "1h 2m".
function lengthOf(seconds: number): string {
  const s = Math.round(seconds)
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

function formatNoteTime(ts: number): string {
  return new Date(ts * 1000).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

interface Props {
  cluster: Cluster | null
  identity: Identity | null
  notes: ClusterNote[]
  onAcknowledge: (id: number, acknowledged: boolean) => Promise<void>
  /**
   * The site keeps no recordings VMS can reach (#294). Acknowledge waits for
   * the incident to finish as anywhere else; only its wording leaves the clip
   * out.
   */
  noRecordings?: boolean
  onAddNote: (id: number, text: string) => Promise<void>
}

// What each tamper signal is, in a dispatcher's terms.
//
// The camera's own rule judged its view obscured. A scene change is the whole
// view changing at once — the camera's only signal for a moved one, and it
// also fires on a cover and on a light being switched on, so this names what
// was observed rather than a cause. Measured on cluster 6182: a camera that
// had been knocked reported a scene change and nothing else, and the card said
// only "tamper". The two sampled kinds are our own frame sampling, the only
// tamper coverage a Frigate camera has; the second says the view is lost
// rather than moved, since a partly covered lens reads exactly like a move.
const tamperSignals: Record<string, string> = {
  detector: "Lens obscured — the camera's own tamper detector reported it",
  scene_change: 'View changed — the camera may have been moved or obscured',
  sampled_obscured: "Lens obscured — the camera's picture lost its detail",
  sampled_moved: 'View lost — the picture no longer shows what this camera was set up to see',
}

function tamperSignal(kind: string): string {
  return tamperSignals[kind] ?? `Reported as ${kind}`
}

export function ClusterDispatchPanel({ cluster, identity, notes, onAcknowledge, onAddNote, noRecordings = false }: Props) {
  if (!cluster) {
    return (
      <aside className="w-80 shrink-0 flex flex-col items-center justify-center gap-2 bg-zinc-900 border-l border-zinc-800 px-4 text-center">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">Action Center</span>
        <p className="text-xs text-zinc-500">Select an event to dispatch.</p>
      </aside>
    )
  }

  // Falls back to the cluster's own fields if a specific detection's identity wasn't resolved.
  const shown: Identity = identity ?? {
    id: cluster.primary_event_id ?? cluster.last_event_id,
    eventTime: cluster.end_time,
    label: cluster.label,
    score: cluster.score,
  }

  return (
    <DispatchForm
      key={cluster.id}
      cluster={cluster}
      identity={shown}
      notes={notes}
      onAcknowledge={onAcknowledge}
      onAddNote={onAddNote}
      noRecordings={noRecordings}
    />
  )
}

function DispatchForm({ cluster, identity, notes, onAcknowledge, onAddNote, noRecordings }: {
  cluster: Cluster
  noRecordings: boolean
  identity: Identity
  notes: ClusterNote[]
  onAcknowledge: Props['onAcknowledge']
  onAddNote: Props['onAddNote']
}) {
  const [draft, setDraft] = useState('')
  const noteId = useId()
  const [savingAck, setSavingAck] = useState(false)
  const [savingNote, setSavingNote] = useState(false)
  const [noteFailed, setNoteFailed] = useState(false)
  // Keyed by identity.id: it can change without cluster.id, so key={cluster.id}
  // on the element won't remount. Derived here rather than reset in an effect.
  const [imgState, setImgState] = useState({ id: identity.id, retry: 0, failed: false })
  const { retry: imgRetry, failed: imgFailed } =
    imgState.id === identity.id ? imgState : { retry: 0, failed: false }
  const [justAcked, setJustAcked] = useState(false)
  // Asking before reopening, and handing focus back to Reopen when not.
  const [reopening, setReopening] = useState(false)
  const [refocusReopen, setRefocusReopen] = useState(false)
  const reopenId = useId()
  const wasAcked = useRef(cluster.acknowledged)
  const notesList = useRef<HTMLDivElement>(null)

  // A verdict needs the footage it is a verdict about. Blocked until the clip is
  // finished — which means the incident ended and stayed ended past the window
  // in which another detection would reopen it. Already-acknowledged incidents
  // are left alone; this gate is about reaching a verdict, not revisiting one.
  // A site with no recordings waits for the same moment, though there is no
  // clip: gated on all_ended instead, it was allowed inside the merge window
  // and held by a detection abandoned after 15 minutes. A tamper keeps its
  // own window.
  const clipReady = useClipReady(cluster)
  const ackBlocked = !cluster.acknowledged && !clipReady

  useEffect(() => {
    const prev = wasAcked.current
    wasAcked.current = cluster.acknowledged
    if (cluster.acknowledged && !prev) {
      setJustAcked(true)
      const t = setTimeout(() => setJustAcked(false), 500)
      return () => clearTimeout(t)
    }
  }, [cluster.acknowledged])

  // Keep the newest note in view as the history grows, by scrolling the list
  // itself: scrollIntoView also scrolled the page, which below a desktop's
  // width carried an opening incident past its clip to the notes (#287).
  useEffect(() => {
    const list = notesList.current
    if (list) list.scrollTop = list.scrollHeight
  }, [notes.length])

  const handleImgError = () => {
    if (imgRetry === 0) {
      setTimeout(() => setImgState({ id: identity.id, retry: 1, failed: false }), 1500)
    } else {
      setImgState({ id: identity.id, retry: imgRetry, failed: true })
    }
  }

  const toggleAck = async () => {
    setSavingAck(true)
    try {
      await onAcknowledge(cluster.id, !cluster.acknowledged)
    } finally {
      setSavingAck(false)
    }
  }

  const submitNote = async () => {
    const text = draft.trim()
    if (!text) return
    setSavingNote(true)
    setNoteFailed(false)
    try {
      await onAddNote(cluster.id, text)
      setDraft('')
    } catch {
      setNoteFailed(true)
    } finally {
      setSavingNote(false)
    }
  }

  const going = isInProgress(cluster)
  const labels = (cluster.labels?.length ? cluster.labels : [cluster.label || 'detection']).map((l) => l.replace(/_/g, ' '))
  const facts: [string, ReactNode, boolean][] = [
    ['Started', clockOf(cluster.start_time), true],
    going
      ? ['Since', `${lengthOf(cluster.end_time - cluster.start_time)} so far · in progress`, true]
      : ['Lasted', lengthOf(cluster.end_time - cluster.start_time), true],
    ['Detections', `${cluster.event_count} · ${labels.join(', ')}`, false],
    ...(cluster.plates?.length
      ? [[cluster.plates.length === 1 ? 'Plate' : 'Plates', <PlateList plates={cluster.plates} />, true] as [string, ReactNode, boolean]]
      : []),
    ['Camera', `${cluster.camera} · ${cluster.site_name || cluster.site_id}`, false],
  ]
  const tamper = cluster.label === 'tamper'

  return (
    <aside aria-label="Action Center" className="w-[22.5rem] shrink-0 flex flex-col gap-4 bg-zinc-900 border-l border-zinc-800 px-[1.125rem] pt-[1.125rem] pb-4 relative overflow-y-auto scrollbar-thin">
      <div className="flex items-center justify-between shrink-0">
        <h2 className="text-base font-semibold text-zinc-100">Incident</h2>
        {cluster.reported_late && (
          <span className="ml-auto mr-2 h-6 px-2.5 inline-flex items-center rounded-full bg-zinc-800 text-[13px] font-semibold text-zinc-300">Reported late</span>
        )}
        {cluster.acknowledged ? (
          <span className="h-6 px-2.5 inline-flex items-center rounded-full bg-live/10 text-[13px] font-semibold text-live">Acknowledged</span>
        ) : (
          <span className="h-6 px-2.5 inline-flex items-center rounded-full bg-orange-500/15 text-[13px] font-semibold text-orange-300">Pending</span>
        )}
      </div>

      <div className="flex gap-3 shrink-0">
        {imgFailed ? (
          <div className="w-28 shrink-0 aspect-video rounded-md bg-zinc-950 flex items-center justify-center text-[11px] text-zinc-400">
            No picture
          </div>
        ) : (
          <img
            key={`${identity.id}-${imgRetry}`}
            src={`${imageUrl(identity.id, identity.eventTime)}${imgRetry ? `&r=${imgRetry}` : ''}`}
            alt={identity.label}
            width={112}
            height={63}
            className="w-28 shrink-0 aspect-video rounded-md object-cover bg-zinc-950"
            onError={handleImgError}
          />
        )}
        {/* The detection as it was when opened: a refined label later must not
            change what the dispatcher clicked on. */}
        <div className="min-w-0 flex flex-col justify-center gap-0.5">
          <div className="text-sm font-semibold capitalize truncate text-zinc-100">
            {identity.label || 'detection'}
            {identity.score > 0 && <span className="ml-1.5 font-mono font-medium text-zinc-400">{Math.round(identity.score * 100)}%</span>}
          </div>
          {cluster.acknowledged && (
            <div className="text-[13px] text-live truncate">
              {cluster.acknowledged_by ? `Acknowledged by ${cluster.acknowledged_by}` : 'Acknowledged'}
            </div>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm shrink-0">
        {facts.map(([term, value, mono]) => (
          <div key={term} className="contents">
            <dt className="text-zinc-400">{term}</dt>
            <dd className={`text-zinc-100 ${typeof value === 'string' ? 'truncate' : ''} ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</dd>
          </div>
        ))}
      </dl>

      {/* Which signal reported it, and what that signal means. Named rather
          than interpreted: a scene change fires on a move, on a cover and on a
          light being switched on, so "camera moved" would claim more than it
          supports. An incident recorded before the kind was stored names no
          signal — its topic was never written down. */}
      {tamper && (
        <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-attention/55 bg-attention/[0.07] shrink-0">
          {(cluster.tamper_kinds ?? []).map((kind) => (
            <p key={kind} className="text-[13px] text-attention leading-snug">{tamperSignal(kind)}</p>
          ))}
          <p className="text-[13px] text-zinc-300 leading-snug">
            You can acknowledge this while the camera is still affected; it stays marked on its tile until the view comes back.
          </p>
        </div>
      )}

      {/* flex-1 without min-h-0: this block grows into spare space but never
          shrinks past its own content. min-h-0 here was the bug — it let the
          block collapse while the composer inside it kept its size, so the
          textarea and button spilled out over the acknowledge control. */}
      <div className="flex flex-col gap-1.5 flex-1">
        <h3 className="shrink-0 text-sm font-semibold text-zinc-100">Notes</h3>

        {/* The one part that gives: it absorbs the panel's slack and scrolls
            internally, so the note history can never push the composer or the
            acknowledge control off the panel. */}
        <div ref={notesList} className="flex flex-col gap-1.5 flex-1 min-h-32 relative overflow-y-auto scrollbar-thin pr-0.5">
          {notes.length === 0 ? (
            <p className="text-[13px] text-zinc-400">No notes yet.</p>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5">
                <div className="flex items-baseline justify-between gap-2 mb-1 text-xs">
                  <span className="font-semibold text-zinc-100 truncate">{n.author}</span>
                  <span className="font-mono text-zinc-400 whitespace-nowrap" title={new Date(n.created_at * 1000).toLocaleString()}>
                    {formatNoteTime(n.created_at)}
                  </span>
                </div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{n.text}</p>
              </div>
            ))
          )}
        </div>

        <label htmlFor={noteId} className="shrink-0 text-[13px] text-zinc-400">Add a note</label>
        <textarea
          id={noteId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          maxLength={4000}
          placeholder="What you did or saw…"
          className="w-full shrink-0 text-sm max-sm:text-base bg-zinc-950 border border-zinc-500 rounded-md px-3 py-2.5 text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400 resize-none"
        />
        {noteFailed && <span role="alert" className="shrink-0 text-[13px] text-fault">The note was not saved. Try again.</span>}
        <button
          onClick={submitNote}
          disabled={savingNote || !draft.trim()}
          className="shrink-0 self-start h-9 px-3.5 rounded-md border border-zinc-800 bg-zinc-900 text-sm text-zinc-100 hover:bg-zinc-800 disabled:opacity-40 transition-colors"
        >
          {savingNote ? 'Saving…' : 'Add note'}
        </button>
      </div>

      {/* Held at the bottom of a phone's screen, below a clip and notes that
          otherwise push it out of reach. */}
      <div className="flex flex-col gap-2 shrink-0 pt-3.5 border-t border-zinc-800 max-sm:sticky max-sm:bottom-0 max-sm:bg-zinc-900 max-sm:pb-4">
        {cluster.acknowledged ? (
          // A state, not a control: one click on it reopened the incident for
          // every dispatcher. Reopening is its own button and asks first (#287).
          <div className="flex flex-col gap-2">
            <div data-ack className={`h-11 flex items-center justify-center gap-2 rounded-md border border-live/50 bg-live/10 text-sm font-bold text-live ${justAcked ? 'animate-ack-complete' : ''}`}>
              <svg aria-hidden="true" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.25}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Acknowledged
            </div>
            {reopening ? (
              <div role="group" aria-labelledby={reopenId} className="flex flex-col gap-2 p-3 rounded-md border border-zinc-700 bg-zinc-950">
                <span id={reopenId} className="text-sm font-semibold text-zinc-100">Reopen this incident?</span>
                <span className="text-[13px] text-zinc-400">It goes back to Pending for every dispatcher.</span>
                <span className="flex justify-end gap-2">
                  <button
                    type="button"
                    autoFocus
                    onClick={() => { setReopening(false); setRefocusReopen(true) }}
                    className="h-9 px-3 rounded-md border border-zinc-700 bg-zinc-800 text-sm font-semibold text-zinc-100 hover:bg-zinc-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={savingAck}
                    onClick={() => { setReopening(false); void toggleAck() }}
                    className="h-9 px-3 rounded-md border border-zinc-500 text-sm font-semibold text-zinc-100 hover:bg-zinc-800 disabled:opacity-40"
                  >
                    {savingAck ? 'Saving…' : 'Reopen'}
                  </button>
                </span>
              </div>
            ) : (
              <button
                type="button"
                autoFocus={refocusReopen}
                onClick={() => setReopening(true)}
                className="self-center h-9 px-3 rounded-md text-sm font-semibold text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
              >
                Reopen
              </button>
            )}
          </div>
        ) : (
          <button
            data-ack
            onClick={toggleAck}
            disabled={savingAck || ackBlocked}
            className="h-11 flex items-center justify-center gap-2 text-sm font-bold rounded-md transition-colors disabled:opacity-40 bg-orange-400 text-zinc-950 hover:bg-orange-300"
          >
            <svg aria-hidden="true" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.25}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            {savingAck ? 'Saving…' : 'Acknowledge'}
          </button>
        )}
        <span className="text-[13px] text-zinc-400 text-center">
          {ackBlocked
            ? noRecordings ? 'Activity ongoing — acknowledge once it ends.' : cluster.all_ended ? 'Preparing clip — acknowledge once it is ready.' : 'Activity ongoing — acknowledge once it settles.'
            : tamper ? 'Acknowledging does not clear the camera.' : 'Marks it handled for every dispatcher.'}
        </span>
      </div>
    </aside>
  )
}

// PlateList shows each plate as sure as it was read (vms#412): the
// characters in doubt as "?" with the full guess on hover, how many reads
// agreed, and that a read may be wrong, so nobody takes it as certain.
function PlateList({ plates }: { plates: Plate[] }) {
  return (
    <div className="flex flex-col gap-0.5">
      {plates.map((p) => (
        <div key={p.text}>
          <span title={p.masked ? `Best guess ${p.text}` : undefined}>{p.masked || p.text}</span>
          {p.state && <span className="font-sans"> ({p.state})</span>}
          {p.reads ? (
            <span className="font-sans text-zinc-400">
              {' · '}
              {p.reads === 1 ? 'read once' : `read the same ${p.votes ?? 0} of ${p.reads} times`}
            </span>
          ) : null}
        </div>
      ))}
      <p className="font-sans text-xs text-zinc-400">Plates are read automatically and may be wrong.</p>
    </div>
  )
}
